/**
 * The live wire between two screens looking at the same data.
 *
 * `./store-events` already tells *this* window that something was written, and
 * `../db/sync-store` already carries a write up to the cloud and everybody
 * else's writes back down. Between them there was still one gap the mandate
 * names: a second window on the same device — the מ"פ with the roster open in
 * one tab and the board in another, the phone that was left on the dashboard —
 * heard nothing until the next poll came round.
 *
 * A `BroadcastChannel` closes it. Every local write is announced on the channel;
 * every other context on the same origin hears it on the same frame and re-reads
 * immediately. It costs one message per write and no network at all.
 *
 * Two properties make it safe to wire into the store bus:
 *
 * - **A tab never hears itself.** Each context stamps its messages with
 *   {@link LIVE_ORIGIN}, and {@link isForeignUpdate} drops its own echo. Without
 *   that, an incoming message would re-announce, which would re-broadcast, which
 *   is a loop that never settles.
 * - **A remote change is applied, never re-pushed.** The receiving side calls
 *   `emitRemoteStoreChange()`, which repaints the screens without raising the
 *   cloud push signal — the device that made the change is already pushing it.
 *
 * Where the API does not exist at all (an old browser, the verification harness
 * on a runtime without it) every function here degrades to a no-op and the
 * application behaves exactly as it did before: the poll still runs, the screens
 * still re-read, the wire is simply not there.
 */
import { emitRemoteStoreChange, installStoreBroadcaster } from './store-events';
import type { StoreDomain } from './store-events';

/** The channel name, scoped to this application alone. */
export const LIVE_CHANNEL_NAME = 'shinua99-live-updates';

/** One announcement, as it travels between contexts. */
export interface LiveUpdate {
  /** Which context sent it — see {@link LIVE_ORIGIN}. */
  origin: string;
  /** The part of the database that moved. */
  domain: StoreDomain;
  /** When it was announced, ISO-8601. */
  at: string;
  /** The operator whose write it was, when the sender knows it. */
  actorName?: string;
}

/** A random id, with a fallback for a runtime that has no `randomUUID`. */
const randomId = (): string => {
  const uuid = (globalThis.crypto as Crypto | undefined)?.randomUUID;
  if (typeof uuid === 'function') return uuid.call(globalThis.crypto);
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

/**
 * This context's identity on the wire — one per tab, per window, per worker.
 * Generated once at module load, which is exactly the lifetime of a context.
 */
export const LIVE_ORIGIN = `live-${randomId()}`;

/** True when the message came from somewhere else and is worth acting on. */
export function isForeignUpdate(
  update: Partial<LiveUpdate> | null | undefined,
  origin: string = LIVE_ORIGIN,
): boolean {
  if (!update || typeof update !== 'object') return false;
  if (!update.domain) return false;
  return Boolean(update.origin) && update.origin !== origin;
}

/** Whether this runtime can carry the wire at all. */
export const liveUpdatesAvailable = (): boolean =>
  typeof globalThis.BroadcastChannel === 'function';

type Listener = (update: LiveUpdate) => void;

let channel: BroadcastChannel | null = null;
const listeners = new Set<Listener>();

/**
 * The channel, opened on first use.
 *
 * `unref()` where the runtime offers it (Node does, browsers do not): an open
 * channel must never be the reason a process that has finished its work stays
 * alive.
 */
function openChannel(): BroadcastChannel | null {
  if (!liveUpdatesAvailable()) return null;
  if (channel) return channel;
  channel = new BroadcastChannel(LIVE_CHANNEL_NAME);
  (channel as unknown as { unref?: () => void }).unref?.();
  channel.onmessage = (event: MessageEvent) => {
    const update = event?.data as LiveUpdate | undefined;
    if (!isForeignUpdate(update)) return;
    for (const listener of [...listeners]) {
      try {
        listener(update as LiveUpdate);
      } catch {
        // A listener's failure is not the sender's problem, nor the next
        // listener's — the announcement carries on to all of them.
      }
    }
  };
  return channel;
}

/**
 * Announces one local write to every other context. Returns whether it went
 * anywhere: `false` on a runtime with no channel, which is not an error.
 */
export function publishLiveUpdate(domain: StoreDomain, actorName?: string): boolean {
  const bus = openChannel();
  if (!bus) return false;
  const update: LiveUpdate = {
    origin: LIVE_ORIGIN,
    domain,
    at: new Date().toISOString(),
    ...(actorName ? { actorName } : {}),
  };
  try {
    bus.postMessage(update);
    return true;
  } catch {
    // A context being torn down mid-write throws here. The data is already
    // saved and the poll will carry it; losing the announcement is the mild
    // failure and taking the write down with it would be the severe one.
    return false;
  }
}

/** Subscribes to other contexts' writes. Returns the unsubscribe. */
export function subscribeToLiveUpdates(listener: Listener): () => void {
  openChannel();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Closes the wire and drops every subscriber. */
export function closeLiveUpdates(): void {
  listeners.clear();
  if (!channel) return;
  try {
    channel.onmessage = null;
    channel.close();
  } catch {
    // Already closed — nothing to do.
  }
  channel = null;
}

/**
 * Joins the wire to the store bus, in both directions:
 *
 * - every local write announced through `emitStoreChange()` is broadcast, and
 * - every announcement from another context repaints this one.
 *
 * Returns the teardown, so a shell that unmounts leaves nothing behind. Calling
 * it on a runtime with no `BroadcastChannel` is safe and does nothing.
 */
export function startLiveUpdates(onForeignUpdate?: (update: LiveUpdate) => void): () => void {
  const uninstall = installStoreBroadcaster((change) => {
    publishLiveUpdate(change.domain);
  });
  const unsubscribe = subscribeToLiveUpdates((update) => {
    // Repaint without re-announcing and without raising the push signal: the
    // context that wrote the record is the one that owes the cloud a push.
    emitRemoteStoreChange(update.domain);
    onForeignUpdate?.(update);
  });
  return () => {
    unsubscribe();
    uninstall();
  };
}
